import React, { FC } from "react";
import { t, Trans } from "@helpers/translate";
import { GetStaticPaths, GetStaticProps } from "next";
import { graphql } from "@helpers/graphql";
import {
  CitiesQuery,
  CountryBySlugQuery,
  ProductType,
  ReviewsQuery,
  TourOrderBy,
  ToursQuery,
} from "@graphql/types";

import { getNodes } from "@helpers/getNodes";
import { searchURL } from "@helpers/searchURL";
import { CACHE_TIME, PLACEHOLDER_IMAGE } from "@constants";
import { isInValidSlug } from "@helpers/isInValidSlug";
import { getMetaDataDescription } from "@helpers/getMetaDataDescription";
import LoadingPage from "@components/pages/LoadingPage";
import Head from "@components/common/Head";
import ProductMicroFormat from "@components/common/MicroFormat/ProductMicroFormat";
import Hero from "@components/common/Hero/Hero";
import { Container } from "@components/ui/Container";
import { CategoriesLinks } from "@components/pages/SearchPage/CategoriesLinks";
import ToursBlock from "@components/common/ToursBlock";
import { ButtonLink } from "@components/ui/Button";
import { CitiesBlock } from "@components/common/LocationBlock/CitiesBlock";
import Stats from "@components/common/Stats";
import SeoContentBlock from "@components/common/SeoContentBlock";
import Reviews from "@components/common/Reviews";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { isInValidCountrySlug } from "@helpers/isInValidCountrySlug";
import { Divider } from "@components/ui/Divider";
import {
  CountryFooterData,
  getCountryFooterData,
} from "@components/common/SEO/utils/getCountryFooterData";
import { SEOCountryLinks } from "@components/common/SEO/SEOCountryLinks";
import ScrollToTop from "@components/ui/ScrollToTop/ScrollToTop";

const getCitiesVariables = (countrySlug: string) => {
  const singaporeCityId = 4386;
  return {
    first: 8,
    filters: {
      COUNTRIES_SLUGS: [countrySlug],
      EXCLUDE_IDS: [singaporeCityId], // do not list cities with singapore id
    },
  };
};

const toursCount = 8;
const getToursVariables = (countrySlug: string) => {
  return {
    first: toursCount,
    filters: {
      PRODUCT_TYPES: [ProductType.Car],
      COUNTRIES_SLUGS: [countrySlug],
      ORDER_BY: TourOrderBy.Recommended,
    },
  };
};

const getReviewsVariables = (COUNTRY_SLUG?: string) => {
  return {
    first: 3,
    filters: {
      COUNTRY_SLUG: COUNTRY_SLUG,
      PRODUCT_TYPES: [ProductType.Car],
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = (params?.country as string).toLocaleLowerCase();

  // This is to prevent un-necessary calls to database when slug is not proper value
  if (isInValidSlug(slug)) {
    return { notFound: true };
  }

  if (isInValidCountrySlug(slug) === false) {
    return { notFound: true };
  }

  const country = await graphql.countryBySlug({
    slug,
    locationStatsFilters: { PRODUCT_TYPES: [ProductType.Car] },
  });

  if (!country.countryBySlug) {
    return { notFound: true };
  }

  const tours = await graphql.tours(getToursVariables(slug));

  if (tours.tours.edges.length === 0) {
    return {
      redirect: {
        destination: "/",
        statusCode: 301,
      },
    };
  }

  return {
    props: {
      initialData: {
        country,
        cities: await graphql.cities(getCitiesVariables(slug)),
        tours,
        reviews: await graphql.reviews(getReviewsVariables(slug)),
        footer: await getCountryFooterData(country.countryBySlug),
      },
    },
    revalidate: CACHE_TIME, //  1 hour cache
  };
};

type Props = {
  initialData: {
    country?: CountryBySlugQuery;
    cities?: CitiesQuery;
    tours: ToursQuery;
    reviews?: ReviewsQuery;
    footer: CountryFooterData;
  };
};

const Page: FC<Props> = ({ initialData }) => {
  if (!initialData) {
    return <LoadingPage />;
  }

  const country = initialData.country?.countryBySlug;
  const countryName = country?.name;
  const isCountryActive = country?.isActive;

  const noIndexFollow =
    !isCountryActive || countryName === "Togo" ? true : false;

  return (
    <>
      <Head
        title={t({
          id: "country.transportation-tours.seo.title",
          message: `${countryName} Private Cars, Van & Transfers`,
          values: {
            countryName: `${countryName}`,
          },
        })}
        description={getMetaDataDescription(
          t({
            id: "country.transportation-tours.seo.description",
            message: `Book a Private Car Service, or Transfers in ${countryName}, ${countryName} with a Private Tour Guide. Save money!, and Book online!`,
            values: {
              countryName: `${countryName}`,
            },
          })
        )}
        openGraph={{
          images: [{ url: country?.picture?.url || "" }],
        }}
        noindex={noIndexFollow}
        nofollow={noIndexFollow}
      />

      <ProductMicroFormat
        productID={`${country?.slug}_country_cars`}
        name={`${countryName} Private Cars, Van & Transfers`}
        description={t({
          id: "country.transportation-tours.seo.description",
          values: {
            countryName: `${countryName}`,
          },
        })}
        image={country?.picture?.serpImage || country?.picture?.url}
        reviewCount={initialData.country?.countryBySlug?.stats?.reviewsCount}
      />
      <Hero
        placeholder={PLACEHOLDER_IMAGE}
        src={country?.picture?.url}
        alt="country image"
        title={t({
          id: "country.private-car-tours.hero.h1",
          message: `${countryName} Private Car Tours`,
          values: {
            countryName: `${countryName}`,
          },
        })}
        subtitle={t({
          id: "country.transportation-tours.hero.h2",
          message: `Hire a Private Driver Tour or Airport, Hotel Transfer in ${countryName}`,
          values: {
            countryName: `${countryName}`,
          },
        })}
        breadcrumb={[
          {
            text: countryName || "",
            href: country?.url || "",
          },
          {
            text: t({
              id: "breadcrumb.car-tours",
              message: "Car Tours",
            }),
            href: `${country?.url}/cars`,
          },
        ]}
        reviewsCount={initialData.country?.countryBySlug?.stats.reviewsCount}
        reviewsRate={initialData.country?.countryBySlug?.stats.reviewsRating}
      />
      <div className="max-w-6xl mx-auto md:pt-4">
        {country && (
          <CategoriesLinks
            filters={{ country: country.slug }}
            className="sticky top-0 z-10 px-4 md:px-8 pb-4 pt-4 bg-white shadow-lg-simple md:shadow-none"
            hasGuides={country.hasSuppliers}
            hasTours={country.hasPrivateTours}
            hasOnlineTours={country.hasOnlineTours}
            hasTransportation={country.hasTransportationTours}
          />
        )}
        <Container className="pt-8 md:pt-0 pb-8 pr-0 md:pr-8">
          <Divider className="mt-4 mb-4 hidden md:flex" />
          {/* Popular Tours */}
          <ToursBlock
            title={t({
              id: "country.block.transportation-tours.title",
              message: `Popular ${countryName} Private Cars`,
              values: {
                countryName: `${countryName}`,
              },
            })}
            data={getNodes(initialData.tours.tours)}
            enableSwipeOnMobile={true}
          >
            {initialData.tours?.tours.totalCount > toursCount && (
              <ButtonLink
                href={getAbsoluteUrl(
                  searchURL({
                    country: country?.slug,
                    t: "transportation",
                  })
                )}
                title="GoWithGuide - View All Private Cars"
                variant="default-outline"
                className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
              >
                <Trans
                  id="country.block.transportation-tours.button"
                  values={{
                    countryName: `${countryName}`,
                  }}
                >
                  View All {countryName} Private Cars
                </Trans>
              </ButtonLink>
            )}
          </ToursBlock>

          {/* Popular Cities */}
          <CitiesBlock
            title={t({
              id: "country.block.popular-destinations.title",
              message: `Popular ${countryName} Tour Destinations`,
              values: {
                countryName: `${countryName}`,
              },
            })}
            data={getNodes(initialData.cities?.cities)}
            className="my-10"
            enableSwipeOnMobile={true}
          >
            {initialData.cities?.cities.pageInfo.hasNextPage && (
              <ButtonLink
                href={getAbsoluteUrl(`${country?.url}/destinations`)}
                variant="default-outline"
                className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
                target="_blank"
                title="GoWithGuide - View All Tour Destinations"
              >
                <Trans
                  id="country.block.popular-destinations.button"
                  values={{
                    countryName: `${countryName}`,
                  }}
                >
                  View All {countryName} Tour Destinations
                </Trans>
              </ButtonLink>
            )}
          </CitiesBlock>

          <div className="pr-4 md:pr-0">
            {/* Reviews */}
            <div id="reviews">
              <Reviews
                type="tours"
                initialData={initialData?.reviews}
                filters={getReviewsVariables(country?.slug)}
                title={t({
                  id: "country.block.review.title",
                  message: `${countryName} Tour Reviews`,
                  values: {
                    countryName: `${countryName}`,
                  },
                })}
              />
            </div>

            <div className="mb-10">
              <Stats />
            </div>

            <SeoContentBlock
              content={country?.seoContent.transportation.content || ""}
              title={country?.seoContent.transportation.title || ""}
            />
            <SEOCountryLinks
              country={initialData.footer.country}
              cities={initialData.footer.cities}
              categories={initialData.footer.categories}
            />
          </div>

          <ScrollToTop />
        </Container>
      </div>
    </>
  );
};

export default Page;
